/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/jsx-no-constructed-context-values */
import React, { createContext, useState, useContext, useCallback, useRef } from 'react';

import { AxiosResponse } from 'axios';
import queryString from 'query-string';
import { useNavigate, useLocation } from 'react-router-dom';

import { isIOS } from 'react-device-detect';
import IOrder from '../models/IOrder';
import ITable from '../models/ITable';

import IPostTableOrderDTO from '../dtos/IPostTableOrderDTO';
import IPostUnauthedOrderDTO from '../dtos/IPostUnauthedOrderDTO';
import { IPostDeliveryOrderDTO } from '../dtos/IPostDeliveryOrderDTO';

import api, { applyErrorRedirect } from '../services/api';
import { normalizeItem } from '../utils/orders';
import { onlyDigits } from '../utils/masks';
import getApiErrorMessage from '../utils/apiError';
import { useToast } from './toast';
import { SessionStorageKeys } from '../enum/Storage';

/**
 * Outcome of `loadTable`:
 * - `loaded`: the table was stored;
 * - `failed`: the table session is still current and the request failed without a page redirect
 *   (no response at all, or a status other than 401, 404 and 5xx);
 * - `redirected`: the table session is still current and the failure sent the page to the same place
 *   the interceptor always did (401, 404, 5xx);
 * - `stale`: another table session started, this one ended, or the caller stopped waiting for the load
 *   (`isStillWanted` answered false, e.g. the loading page was left), while the request was in flight.
 *   The answer was dropped without writing anything and without navigating.
 */
export type LoadTableResult = 'loaded' | 'failed' | 'stale' | 'redirected';

/**
 * `isStillWanted`, when given, is asked after the request, right before any write or redirect, and must
 * answer synchronously whether the caller still waits for this load. Leaving the loading page does not
 * change the table session, so the epoch alone cannot tell.
 */
type LoadTable = (
  hash?: string,
  refreshSession?: boolean,
  isStillWanted?: () => boolean,
) => Promise<LoadTableResult>;

interface OrderContextData {
  table: ITable;
  /**
   * Identifies the current table session. Bumped once when a session starts from a QR read
   * (`beginTableSession`) and when one ends (`resetTable`); loading the table never changes it. Part of
   * the rodízio gate key, so an answer about one session never applies to the next.
   */
  tableSessionRevision: number;
  /**
   * Ends whatever table session was there and starts a new one, before its table is loaded: clears the
   * table and both session keys, and advances the session. Called once per logical QR read, by the
   * loading page only, and it does not navigate.
   */
  beginTableSession: () => void;
  resetTable: (quit?: boolean) => void;
  loadTable: LoadTable;
  postDeliveryOrder: (data: IPostDeliveryOrderDTO) => Promise<IOrder>;
  postTableOrder: (data: IPostTableOrderDTO) => Promise<AxiosResponse<IOrder>>;
  postTableOrderLocal: ({
    items,
    cardNo,
    baseURL,
    tableNo,
    receiverName,
  }: IPostTableOrderDTO) => Promise<string | AxiosResponse<any, any>>;
  postUnauthedDeliveryOrder: (data: IPostUnauthedOrderDTO) => Promise<IOrder>;
}

const OrderContext = createContext<OrderContextData>({} as OrderContextData);

interface IOrderProvider {
  children: React.ReactNode;
}
export const OrderProvider: React.FC<IOrderProvider> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { addToast } = useToast();

  const [table, setTable] = useState<ITable>(() => {
    const previous = sessionStorage.getItem('@BSFOOD:table');

    if (previous) {
      return JSON.parse(previous) as ITable;
    }

    return {} as ITable;
  });

  // The session epoch is read synchronously after an await, so it lives in a ref; the revision is the
  // same number as state, for the consumers that render from it. They always move together.
  const sessionEpochRef = useRef(0);
  const [tableSessionRevision, setTableSessionRevision] = useState(0);

  const advanceTableSession = useCallback(() => {
    sessionEpochRef.current += 1;
    setTableSessionRevision(sessionEpochRef.current);
  }, []);

  const beginTableSession = useCallback(() => {
    sessionStorage.removeItem('@BSFOOD:table');
    sessionStorage.removeItem('@BSFOOD:table_session');
    setTable({} as ITable);
    advanceTableSession();
  }, [advanceTableSession]);

  const loadTable = useCallback<LoadTable>(
    async (hash, refreshSession, isStillWanted): Promise<LoadTableResult> => {
      // Still defaults to true; a default parameter can no longer be written before `isStillWanted`.
      const shouldRefreshSession = refreshSession ?? true;

      // Captured before the request. If a session starts or ends while it is in flight, the answer
      // belongs to a session that no longer exists: writing it back would restore the old table, and
      // with it the old card number, inside the new session.
      const epoch = sessionEpochRef.current;

      // Leaving the loading page does not change the session, so the caller also says whether it still
      // waits: an abandoned load must not write the table back nor redirect the customer from wherever
      // they went. Both are read synchronously at each checkpoint, never through a deferred flag: the
      // continuation of the request can already be queued when the page is left.
      const isCurrent = () =>
        epoch === sessionEpochRef.current && (isStillWanted ? isStillWanted() : true);

      try {
        const parsed = queryString.parse(location.search);

        // The interceptor would redirect before the catch below runs, even for a session that was
        // already replaced. The same redirects are applied there, after the epoch check.
        //
        // The request itself is never cancelled: the default retry condition of axios-retry 3.x treats
        // a cancelled GET (no response, code ERR_CANCELED) as an idempotent request error and retries
        // it, so cancelling would turn one request into a train of failures. Its answer is dropped.
        const response = await api.get<ITable>(`/hash?token=${hash || parsed.token?.toString()}`, {
          skipErrorRedirect: true,
        });

        if (!isCurrent()) return 'stale';

        sessionStorage.setItem(
          '@BSFOOD:table',
          JSON.stringify({
            tableToken: parsed.token?.toString(),
            tableNumber: response.data.tableNumber,
          }),
        );

        setTable({ ...response.data, tableToken: parsed.token?.toString() || '' });

        if (shouldRefreshSession) {
          console.log('Session Reseted');
          sessionStorage.setItem('@BSFOOD:table_session', Date.now().toString());
        }

        return 'loaded';
      } catch (err) {
        // First, before any handling: a failure of a replaced session or of an abandoned load is not an
        // error to report, and it must not navigate.
        if (!isCurrent()) return 'stale';

        console.log((err as any)?.message);

        return applyErrorRedirect(err) ? 'redirected' : 'failed';
      }
    }, // eslint-disable-next-line @typescript-eslint/indent
    [location.search],
  );

  const postTableOrderLocal = useCallback(
    async ({ items, tableNo, baseURL, cardNo, receiverName, phoneNumber }: IPostTableOrderDTO) => {
      // Digits only, capped at the Brazilian functional length (DDD + 9). The destination column
      // mko_phone_number is narrow and MySQL runs in STRICT mode there, so an oversized string
      // raises error 1406 and takes the whole order transaction down with it. Empty becomes
      // undefined so the column stays NULL instead of holding an empty string.
      const normalizedPhone = onlyDigits(phoneNumber).slice(0, 11) || undefined;

      try {
        const response = await api.post(
          '/orders/bsfood',
          {
            items: items.map(normalizeItem),
            tableNo,
            cardNo,
            receiverName: receiverName !== '%' ? receiverName : '',
            phoneNumber: normalizedPhone,
            orderType: 'ON_TABLE',
          },
          {
            baseURL,
          },
        );

        if (response.data?.success === false || response.data?.error) {
          throw new Error(response.data?.message || 'Erro ao enviar pedido');
        }

        // if (response?.data?.id) {
        //   sessionStorage.setItem(SessionStorageKeys.ORDER_TABLE_ID, response.data?.id);
        // }

        return response;
      } catch (error) {
        const message = getApiErrorMessage(error);
        addToast({
          type: 'error',
          description: message || 'Ocorreu um erro inesperado.',
        });
        return message;
      }
    },
    [addToast],
  );

  const postTableOrder = useCallback(
    async ({ items, tableNo, personAmount, baseURL }: IPostTableOrderDTO) => {
      return api.post<IOrder>(
        '/table-orders',
        {
          items: items.map(normalizeItem),
          tableNo,
          personAmount,
          orderType: 'ON_TABLE',
        },
        {
          baseURL,
        },
      );
    },
    [],
  );

  const postDeliveryOrder = useCallback(
    async ({
      type,
      total,
      items,
      address,
      comments,
      couponId,
      discount,
      customer,
      cashAdvance,
      deliveryFee,
      paymentType,
      phoneNumber,
      deliveryType,
      addressDefault,
      deliveryPlaceId,
      receiverDocument,
      scheduledProduct,
      cardBrandId,
    }: IPostDeliveryOrderDTO) => {
      const response = await api.post('/external-orders', {
        items: items.map(normalizeItem),
        total,
        comments,
        discount,
        deliveryFee,
        paymentType,
        deliveryType,
        addressDefault,
        scheduledProduct,
        cardBrandId,
        orderType: type,
        receiverName: customer?.name,
        cashAdvance: Number(cashAdvance),
        city: address.city,
        state: address.state,
        addressId: address.id,
        address2: address.address2,
        cityIbgeCode: address.ibgeCode,
        postalCode: address.postalCode,
        streetName: address.streetName,
        phoneNumber: address.phoneNumber,
        neighborhood: address.neighborhood,
        streetNumber: address.streetNumber,
        addressReference: address.reference,
        ...(couponId > 0 && { couponId }),
        ...(deliveryPlaceId > 0 && { deliveryPlaceId }),
        ...(receiverDocument && { receiverDocument }),
        ...(phoneNumber && { phoneNumber }),
      });

      return response.data;
    },
    [],
  );

  const postUnauthedDeliveryOrder = useCallback(
    async ({
      type,
      total,
      items,
      address,
      comments,
      discount,
      cashAdvance,
      deliveryFee,
      paymentType,
      phoneNumber,
      customerName,
      deliveryType,
      deliveryPlaceId,
      receiverDocument,
      scheduledProduct,
      cardBrandId,
    }: IPostUnauthedOrderDTO) => {
      const response = await api.post('/external-orders/unauthed', {
        items: items.map(normalizeItem),
        total,
        comments,
        discount,
        deliveryFee,
        paymentType,
        deliveryType,
        scheduledProduct,
        cardBrandId,
        orderType: type,
        receiverName: customerName,
        cashAdvance: Number(cashAdvance),
        city: address.city,
        state: address.state,
        addressId: address.id,
        address2: address.address2,
        cityIbgeCode: address.ibgeCode,
        postalCode: address.postalCode,
        streetName: address.streetName,
        phoneNumber: address.phoneNumber,
        neighborhood: address.neighborhood,
        streetNumber: address.streetNumber,
        addressReference: address.reference,
        ...(address.lat && { addressLat: address.lat }),
        ...(address.lng && { addressLng: address.lng }),
        ...(deliveryPlaceId > 0 && { deliveryPlaceId }),
        ...(receiverDocument && { receiverDocument }),
        ...(phoneNumber && { phoneNumber }),
      });

      return response.data;
    },
    [],
  );

  const resetTable = useCallback(() => {
    advanceTableSession();
    sessionStorage.removeItem('@BSFOOD:table');
    sessionStorage.removeItem('@BSFOOD:table_session');
    setTable({} as ITable);

    if (isIOS) {
      // No iOS: navega na mesma aba mas substitui o histórico
      navigate('/table', {
        replace: true,
        state: { fromIOS: true, expired: table.tableNumber > 0 },
      });

      // Não conseguimos fechar a aba anterior, mas podemos
      // desabilitar a navegação de volta
    } else {
      navigate('/table', {
        replace: true,
        state: { expired: table.tableNumber > 0 },
      });
    }
  }, [navigate, advanceTableSession]);

  return (
    <OrderContext.Provider
      value={{
        table,
        tableSessionRevision,
        beginTableSession,
        loadTable,
        resetTable,
        postTableOrder,
        postDeliveryOrder,
        postTableOrderLocal,
        postUnauthedDeliveryOrder,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};

export function useOrder(): OrderContextData {
  const context = useContext(OrderContext);

  if (!context) {
    throw new Error('useOrder must be used within OrderProvider');
  }

  return context;
}
